import type {
  Breed,
  Breeder,
  BreederPet,
  Listing,
  MediaAsset,
  Pet,
  Post,
  Product,
  ProductCategory,
  ProductVariant,
  User,
} from 'shared/@generated/models';
import { getForSalePets } from 'shared/library/pet-roles';

type MediaSource =
  | MediaAsset
  | null
  | undefined
  | Array<MediaAsset | null | undefined>
  | null
  | undefined;

function getAssetsFromSource(source: MediaSource): MediaAsset[] {
  if (!source) {
    return [];
  }

  if (Array.isArray(source)) {
    return source.filter((asset): asset is MediaAsset => !!asset?.url);
  }

  return source.url ? [source] : [];
}

export function collectMediaAssets(...sources: MediaSource[]): MediaAsset[] {
  const seen = new Set<string>();
  const assets: MediaAsset[] = [];

  for (const source of sources) {
    for (const asset of getAssetsFromSource(source)) {
      const key = asset.uuid || asset.url;

      if (!key || seen.has(key)) {
        continue;
      }

      seen.add(key);
      assets.push(asset);
    }
  }

  return assets;
}

export function getFirstMediaAssetUrl(...sources: MediaSource[]): string | null {
  return collectMediaAssets(...sources)[0]?.url ?? null;
}

function getVariantAssets(variants: ProductVariant[] | null | undefined): MediaAsset[] {
  return (variants ?? []).flatMap((variant) =>
    collectMediaAssets(variant.avatar, variant.media_assets, variant.owned_media_assets),
  );
}

export function getUserImage(user: User | null | undefined): string | null {
  return getFirstMediaAssetUrl(user?.avatar, user?.media_assets, user?.owned_media_assets);
}

export function getBreedImage(breed: Breed | null | undefined): string | null {
  return getBreedImageAsset(breed)?.url ?? null;
}

export function getBreedImageAsset(breed: Breed | null | undefined): MediaAsset | null {
  return (
    collectMediaAssets(breed?.avatar, breed?.media_assets, breed?.owned_media_assets)[0] ?? null
  );
}

export function getBreederPetImage(pet: BreederPet | Pet | null | undefined): string | null {
  return getBreederPetImageAsset(pet)?.url ?? null;
}

export function getBreederPetImageAsset(
  pet: BreederPet | Pet | null | undefined,
): MediaAsset | null {
  return collectMediaAssets(pet?.avatar, pet?.media_assets, pet?.owned_media_assets)[0] ?? null;
}

export function getBreederImage(breeder: Breeder | null | undefined): string | null {
  return getBreederImageAsset(breeder)?.url ?? null;
}

export function getBreederImageAsset(breeder: Breeder | null | undefined): MediaAsset | null {
  return (
    collectMediaAssets(
      breeder?.avatar,
      breeder?.media_assets,
      breeder?.owned_media_assets,
      breeder?.owners?.map((owner) => owner.avatar),
      breeder?.users?.map((user) => user.avatar),
      breeder?.breeder_pets?.map((pet) => pet.avatar),
      breeder?.breeder_pets?.flatMap((pet) => [
        ...(pet.media_assets ?? []),
        ...(pet.owned_media_assets ?? []),
      ]),
      breeder?.breeds?.map((breed) => breed.avatar),
      breeder?.breeds?.flatMap((breed) => [
        ...(breed.media_assets ?? []),
        ...(breed.owned_media_assets ?? []),
      ]),
    )[0] ?? null
  );
}

export function getBreederGalleryAssets(breeder: Breeder | null | undefined): MediaAsset[] {
  return collectMediaAssets(
    breeder?.avatar,
    breeder?.media_assets,
    breeder?.owned_media_assets,
    breeder?.breeder_pets?.map((pet) => pet.avatar),
    breeder?.breeder_pets?.flatMap((pet) => [
      ...(pet.media_assets ?? []),
      ...(pet.owned_media_assets ?? []),
    ]),
    breeder?.breeds?.map((breed) => breed.avatar),
    breeder?.breeds?.flatMap((breed) => [
      ...(breed.media_assets ?? []),
      ...(breed.owned_media_assets ?? []),
    ]),
  );
}

export function getListingImage(listing: Listing | null | undefined): string | null {
  return getListingImageAsset(listing)?.url ?? null;
}

export function getListingImageAsset(listing: Listing | null | undefined): MediaAsset | null {
  const forSalePets = getForSalePets(listing?.pets ?? []);

  return (
    collectMediaAssets(
      listing?.avatar,
      listing?.media_assets,
      listing?.owned_media_assets,
      forSalePets.map((pet) => pet.avatar),
      forSalePets.flatMap((pet) => [
        ...(pet.media_assets ?? []),
        ...(pet.owned_media_assets ?? []),
      ]),
    )[0] ?? null
  );
}

export function getProductImage(product: Product | null | undefined): string | null {
  return getFirstMediaAssetUrl(
    product?.avatar,
    product?.media_assets,
    product?.owned_media_assets,
    getVariantAssets(product?.product_variants),
  );
}

export function getProductGalleryAssets(product: Product | null | undefined): MediaAsset[] {
  return collectMediaAssets(
    product?.avatar,
    product?.media_assets,
    product?.owned_media_assets,
    getVariantAssets(product?.product_variants),
  );
}

export function getProductCategoryImage(
  category: ProductCategory | null | undefined,
): string | null {
  return getFirstMediaAssetUrl(
    category?.avatar,
    category?.media_assets,
    category?.owned_media_assets,
  );
}

export function getPostImage(post: Post | null | undefined): string | null {
  return getFirstMediaAssetUrl(post?.avatar, post?.media_assets, post?.owned_media_assets);
}
